import { getCourseResources, getCourses } from 'core/crud/courses.crud';
import { useOrganization } from 'core/hooks/useOrganization';
import useSystemSettings from 'core/hooks/useSystemSettings';
import { useUser } from 'core/lite/hooks/useUser';
import { getCourseItemsService } from 'core/services/courseItems.service';
import * as coreEntities from 'core/store/index';
import { COURSE_TYPES } from 'core/utils/constants/course';
import { getCustomizedBy, getGuidCourseProgram } from 'core/utils/course';
import shouldRenderPowerLessonView from 'core/utils/shouldRenderPowerLessonView';
import { decodeHtmlWithWhiteList } from 'core/utils/strings';
import { successResponse } from 'core/utils/utils';
import { CLIENT, CONTENT_TYPES, LESSON_TYPES_IDS } from 'core/variables/constant';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { userDataService } from '../../../services';
import { LOCATION_DIALOG_TARGETS } from './locationDialogTargets';

// Referencia estable: evita recalcular los memos mientras las peticiones no han resuelto.
const NO_ITEMS = [];

/**
 * Aplana los grupos de las secciones de tipo test en opciones de select (Edelvives).
 * @param {Array} sections - Secciones de recursos del curso
 * @returns {Array} Opciones de grupo de evaluación
 */
function parseTestGroups(sections = []) {
  return sections
    .filter((section) => section.type === 'test')
    .flatMap((section) =>
      (section.items ?? []).map((group) => ({
        value: `${section.guid}|${group.guid}`,
        label: group.name || section.name,
        sectionGuid: section.guid,
        groupGuid: group.guid,
        data: { section, group },
      }))
    );
}

/**
 * Nombre del libro. Fuente única para la etiqueta y la ordenación: si divergen, la lista se
 * ordena por un criterio distinto del que muestra.
 */
function getProgramName(item) {
  return item.name || item.title;
}

/**
 * Una fila de front/courses es programa x grupo: el nombre del libro no la identifica por sí
 * solo. Mismo orden que el dropdown de cursos del header (grupo primero).
 */
function getLocationLabel(item) {
  const programName = getProgramName(item);
  return item.school_group_name ? `${item.school_group_name} · ${programName}` : programName;
}

/**
 * Convención del repo (core/utils/parses.js): `numeric` evita que «10ºA» preceda a «5ºA».
 */
function compareNames(a, b) {
  return (a ?? '').localeCompare(b ?? '', undefined, { numeric: true, sensitivity: 'base' });
}

/** Sin `preview=1` las vistas piden el detalle como si hubiera grupo de clase → 403. */
function buildLocationUrl(path, queryParams, shouldUsePreview) {
  const params = shouldUsePreview ? [...queryParams, 'preview=1'] : queryParams;
  return params.length > 0 ? `${path}?${params.join('&')}` : path;
}

/**
 * Target `mint`: URL of the lesson that opens the HTML page prefilled with the conversation, or
 * null while the selection is incomplete or no longer among the lessons that can take the page.
 */
function getMintNavigationUrl({ conversationId, courseGuid, lessons, offeredUnits, selectedLesson, selectedUnit, shouldUsePreview }) {
  if (!conversationId || !offeredUnits.includes(selectedUnit.data)) return null;
  if (lessons.length > 0 && !lessons.includes(selectedLesson?.data)) return null;

  // Without lessons the unit itself is the lesson.
  const lessonGuid = lessons.length > 0 ? selectedLesson.data.lesson_guid || selectedLesson.value : selectedUnit.data?.lesson_guid;
  if (!lessonGuid) return null;
  return buildLocationUrl(`/course/${courseGuid}/lesson/${lessonGuid}`, [`addMintPage=${encodeURIComponent(conversationId)}`], shouldUsePreview);
}

/**
 * Guids de los cursos con IA del usuario (`user/courses` de Tangerine AI), para saber qué libros de la
 * biblioteca la tienen activa: si un libro sale ahí, la tiene. Una llamada por apertura del diálogo.
 */
function useAiCourseIds({ isEnabled, isOpen, hasLibraryPrograms }) {
  // `null` mientras no se sabe en esta apertura.
  const [aiCourseIds, setAiCourseIds] = useState(null);
  // Cambia al cerrar: la respuesta tardía de una apertura anterior no decide la actual.
  const openingRef = useRef(0);

  useEffect(() => {
    if (isOpen) return;
    // Al reabrir se pregunta de nuevo: la IA de un libro puede haber cambiado con el diálogo cerrado.
    openingRef.current += 1;
    setAiCourseIds(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isEnabled || !hasLibraryPrograms) return;
    const opening = openingRef.current;

    // Depende solo de booleanos: un refresco del store no vuelve a preguntar. La respuesta se descarta al
    // cerrar el diálogo (openingRef), no al limpiar el efecto.
    async function loadAiCourseIds() {
      let ids;
      try {
        const courses = await userDataService.getCourses();
        ids = new Set(courses.map((course) => course.id));
      } catch {
        // Sin saber qué libros tienen IA no se ofrece ninguno: es el caso que bloqueaba al profesor.
        ids = new Set();
      }
      if (opening !== openingRef.current) return;
      setAiCourseIds(ids);
    }

    loadAiCourseIds();
  }, [isOpen, isEnabled, hasLibraryPrograms]);

  const isLoadingCourses = isOpen && isEnabled && hasLibraryPrograms && aiCourseIds === null;

  return { aiCourseIds, isLoadingCourses };
}

/**
 * Edelvives guarda el quiz en la caja de evaluaciones, salvo en las plantillas fija e híbrida: su
 * LessonPage solo pinta esas, con LessonViewPower, que lo guarda en la lección. Con cualquier otra
 * plantilla la lección se quedaría en blanco aunque el curso tenga `has_power_lesson`. Hasta que llega
 * el detalle del curso no se elige; si falla, la caja de evaluaciones, como antes de mirarlo.
 */
function getEdelvivesQuizFlow({ hasLoadedCourseDetail, hasSelectedCourse, isEdelvivesQuiz, templateType }) {
  if (!isEdelvivesQuiz || !hasSelectedCourse) return { isToolboxFlow: false, isWaitingForFlow: false };
  if (!hasLoadedCourseDetail) return { isToolboxFlow: false, isWaitingForFlow: true };
  return { isToolboxFlow: !shouldRenderPowerLessonView({ client: CLIENT.EDELVIVES, templateType }), isWaitingForFlow: false };
}

/**
 * Custom hook for managing quiz location selection dialog state and logic
 * @param {Object} params
 * @param {boolean} params.isOpen - Whether the dialog is open
 * @param {Function} params.onClose - Closes the dialog
 * @param {Function} [params.onNavigate] - Called right before navigating to the quiz creation page
 * @param {'quiz'|'mint'} [params.target] - What is placed in the lesson: a quiz, or a Tutor AI document as an HTML page
 * @param {string} [params.conversationId] - Tutor AI conversation whose document becomes the page (target `mint`)
 * @returns {Object} State and handlers for the quiz location dialog
 */
function useQuizLocationDialog({ isOpen, onClose, onNavigate, target = LOCATION_DIALOG_TARGETS.QUIZ, conversationId = null }) {
  const { t } = useTranslation(['chatbot', 'actions']);
  const dispatch = useDispatch();
  const history = useHistory();
  const { client, tenantConfig } = useOrganization();
  const { enableOverlays } = useSystemSettings();
  const { schoolGuid, user } = useUser();

  const isMintTarget = target === LOCATION_DIALOG_TARGETS.MINT;
  const isQuizTarget = target === LOCATION_DIALOG_TARGETS.QUIZ;
  const isEdelvivesTenant = client === CLIENT.EDELVIVES;

  // Los tenants con integraciones de terceros ocultan los programas licenciados sin clase en
  // el resto del producto. Solo aplica a `front/programs`: ocultar además las clases sin grupo
  // quitaría una ubicación que hoy funciona.
  const shouldShowPrograms = tenantConfig?.showDisregardedPrograms ?? true;

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);

  const isPowerCourse = Number(selectedCourse?.data?.has_power_lesson) === 1;
  const isEdelvivesQuiz = isEdelvivesTenant && isQuizTarget;

  const [units, setUnits] = useState([]);

  // Edelvives: Test sections for deep link
  const [testSections, setTestSections] = useState([]);
  const [selectedTestGroup, setSelectedTestGroup] = useState(null);
  const [hasTestSection, setHasTestSection] = useState(false);

  // El detalle del curso dice qué lecciones abren con LessonViewPower (target `mint`) y, en Edelvives,
  // dónde va el quiz. Las filas de clases no traen `template` ni heredan `has_power_lesson` del
  // programa, y las de la biblioteca no traen ninguno de los dos (TANAI-2811).
  const [courseDetail, setCourseDetail] = useState(null);
  const [isLoadingCourseDetail, setIsLoadingCourseDetail] = useState(false);
  const [hasLoadedCourseDetail, setHasLoadedCourseDetail] = useState(false);

  const { isToolboxFlow, isWaitingForFlow } = getEdelvivesQuizFlow({
    hasLoadedCourseDetail,
    hasSelectedCourse: Boolean(selectedCourse),
    isEdelvivesQuiz,
    templateType: courseDetail?.templateType,
  });

  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [isLoadingTestSections, setIsLoadingTestSections] = useState(false);
  const [hasLoadedUnits, setHasLoadedUnits] = useState(false);
  const [hasLoadedTestSections, setHasLoadedTestSections] = useState(false);

  // Las clases (`front/courses`) son programa x grupo: un profesor con licencia de programa y
  // sin grupo no tiene ninguna, así que se le ofrecen también sus programas.
  const classes = useSelector((state) => coreEntities.courses.selectors.getCourses(state)) ?? NO_ITEMS;
  // El selector reconstruye su resultado en cada llamada: con la lista vacía daría un array nuevo por render.
  const programs = useSelector((state) => {
    const items = coreEntities.programs.selectors.getPrograms(state)?.programs;
    return items?.length ? items : NO_ITEMS;
  });

  useEffect(() => {
    dispatch(coreEntities.courses.actions.getCourses({ params: { offset: 0, pageSize: 1000 } }));
    dispatch(coreEntities.programs.actions.getPrograms({ isBook: 1, orderBy: 't.name + asc ' }));
  }, [dispatch]);

  // Libros de la biblioteca: los programas licenciados que no tienen ya una clase. El guid de
  // programa de una clase depende de su tipo, así que se dedupe contra los dos.
  const libraryPrograms = useMemo(() => {
    if (!shouldShowPrograms) return NO_ITEMS;

    const knownProgramGuids = new Set(classes.flatMap((item) => [item.program_guid, getGuidCourseProgram(item)].filter(Boolean)));
    const items = [];
    programs.forEach((item) => {
      const programGuid = item.program?.guid ?? item.guid;
      if (!programGuid || knownProgramGuids.has(programGuid)) return;
      knownProgramGuids.add(programGuid);
      // El resto del flujo trabaja con el guid del programa, como parsePrograms.
      items.push({ ...item, guid: programGuid });
    });
    return items;
  }, [classes, programs, shouldShowPrograms]);

  // El formulario del quiz solo ofrece la IA si el curso tiene `has_tangerine_ai`: un libro sin ella
  // dejaba al profesor bloqueado en la siguiente pantalla (TANAI-2849). Las filas de clases traen el
  // flag; las de `front/programs` no, así que se cotejan con los cursos con IA del usuario.
  // TODO(TAN-12556): cuando `front/programs` traiga `has_tangerine_ai`, filtrar con él y quitar esta petición.
  const { aiCourseIds, isLoadingCourses } = useAiCourseIds({ isEnabled: isQuizTarget, isOpen, hasLibraryPrograms: libraryPrograms.length > 0 });

  // Se clasifica por si la fila tiene grupo de clase, no por el origen de los datos:
  // `front/courses` también devuelve filas sin `school_group_guid` (igual que useCourseItems).
  const { classOptions, programOptions } = useMemo(() => {
    const withGroup = [];
    const withoutGroup = [];

    classes.forEach((item) => {
      if (isQuizTarget && Number(item.has_tangerine_ai) !== 1) return;

      const option = {
        // Una clase sin grupo conserva su guid de curso: es el que esperan los endpoints.
        value: item.guid,
        label: getLocationLabel(item),
        data: item,
        shouldUsePreview: !item.school_group_guid,
      };

      if (option.shouldUsePreview) withoutGroup.push(option);
      else withGroup.push(option);
    });

    libraryPrograms.forEach((item) => {
      if (isQuizTarget && !aiCourseIds?.has(item.guid)) return;

      withoutGroup.push({
        value: item.guid,
        label: item.program?.name ?? item.name,
        data: item,
        // Se abre por el guid del programa, no por el de una clase: siempre necesita preview.
        shouldUsePreview: true,
      });
    });

    // Las clases, por grupo y a igual grupo por libro. El cubo sin grupo mezcla dos orígenes,
    // así que se ordena entero. Siempre sobre los arrays locales, nunca sobre el de redux.
    withGroup.sort((a, b) => compareNames(a.data.school_group_name, b.data.school_group_name) || compareNames(getProgramName(a.data), getProgramName(b.data)));
    withoutGroup.sort((a, b) => compareNames(a.label, b.label));

    return { classOptions: withGroup, programOptions: withoutGroup };
  }, [classes, libraryPrograms, isQuizTarget, aiCourseIds]);

  // Con un solo tipo de ubicación las cabeceras de sección no aportan nada.
  const courseOptions = useMemo(() => {
    if (classOptions.length === 0 || programOptions.length === 0) return [...classOptions, ...programOptions];

    return [
      { label: t('chatbot:quiz_location_group_classes'), options: classOptions },
      { label: t('chatbot:quiz_location_group_programs'), options: programOptions },
    ];
  }, [classOptions, programOptions, t]);

  // Target `mint`: only the lessons where the teacher can add an HTML page, the ones that open
  // with LessonViewPower. The list is the fallback when the course detail cannot be loaded.
  const isPowerLesson = useCallback(
    (lesson) =>
      shouldRenderPowerLessonView({
        client: isEdelvivesTenant ? CLIENT.EDELVIVES : client,
        hasPowerLesson: courseDetail?.hasPowerLesson ?? selectedCourse?.data?.has_power_lesson,
        lessonType: lesson?.lesson_type,
        templateType: courseDetail?.templateType ?? selectedCourse?.data?.template?.template_type,
      }),
    [client, courseDetail, isEdelvivesTenant, selectedCourse]
  );

  // MINT queda fuera: Edelvives usa otro flujo.
  const filterLessons = useCallback(
    (unit) => {
      const unitLessons = unit?.data?.items ?? NO_ITEMS;
      return unitLessons.filter((lesson) => (isMintTarget ? isPowerLesson(lesson) : lesson.lesson_type !== LESSON_TYPES_IDS.MINT));
    },
    [isMintTarget, isPowerLesson]
  );

  const lessons = useMemo(() => filterLessons(selectedUnit), [filterLessons, selectedUnit]);

  useEffect(() => {
    if (!selectedCourse || isToolboxFlow || isWaitingForFlow) return undefined;

    // enableOverlays decide el endpoint (overlays vs base): esperar a que los system settings
    // resuelvan para no cargar la versión equivocada del programa.
    if (enableOverlays === undefined) return undefined;

    // Evita escribir estado obsoleto si se cambia de ubicación antes de que resuelva
    let isCancelled = false;

    async function loadCourseUnits() {
      setIsLoadingUnits(true);
      setUnits([]);

      try {
        const course = selectedCourse.data ?? {};
        // Los items de `front/programs` no traen `users`; getCustomizedBy cae entonces en el
        // guid del usuario actual, que es justo lo que necesita el endpoint de overlays.
        const customizedBy = getCustomizedBy(course, user) ?? [];
        // Sin `preview` el backend responde 403 en las ubicaciones sin grupo y en los programas
        // licenciados, y el interceptor global de axios desloguea al profesor.
        const isDisregarded = selectedCourse.shouldUsePreview;
        const response = await getCourseItemsService(selectedCourse.value, schoolGuid, customizedBy, enableOverlays, isDisregarded);

        if (isCancelled) return;
        setUnits(Array.isArray(response) ? response : (response?.items ?? []));
      } catch (error) {
        if (isCancelled) return;
        console.error('[QuizLocationDialog] Error loading units:', error);
        setUnits([]);
      } finally {
        // Se apaga siempre: si no, al cancelar por cierre el spinner sobrevive a la reapertura.
        setIsLoadingUnits(false);
        if (!isCancelled) setHasLoadedUnits(true);
      }
    }

    loadCourseUnits();

    return () => {
      isCancelled = true;
    };
    // Se depende del guid y no de la referencia: un selector que devuelva copia relanzaría.
  }, [selectedCourse, isToolboxFlow, isWaitingForFlow, enableOverlays, schoolGuid, user?.guid]);

  useEffect(() => {
    if (!(isMintTarget || isEdelvivesTenant) || !selectedCourse) return undefined;

    let isCancelled = false;

    async function loadCourseDetail() {
      setIsLoadingCourseDetail(true);
      setCourseDetail(null);

      try {
        // Same request as the lesson page (useCourse): school programs are read in the school.
        const isSchoolProgram = Boolean(selectedCourse.data?.is_school_program);
        const response = await getCourses({ courseGuid: selectedCourse.value, schoolGuid: isSchoolProgram ? schoolGuid : null });
        if (isCancelled || !successResponse(response)) return;
        const course = response.data.data;
        setCourseDetail({ hasPowerLesson: course?.has_power_lesson, templateType: course?.template?.template_type ?? null });
      } catch (error) {
        if (isCancelled) return;
        console.error('[QuizLocationDialog] Error loading course detail:', error);
      } finally {
        if (!isCancelled) {
          setIsLoadingCourseDetail(false);
          setHasLoadedCourseDetail(true);
        }
      }
    }

    loadCourseDetail();

    return () => {
      isCancelled = true;
      setIsLoadingCourseDetail(false);
    };
  }, [isMintTarget, isEdelvivesTenant, selectedCourse, schoolGuid]);

  // Load test sections for the Edelvives deep link
  useEffect(() => {
    if (!isToolboxFlow || !selectedCourse) return undefined;

    let isCancelled = false;

    async function loadTestSections() {
      setIsLoadingTestSections(true);
      setTestSections([]);
      setSelectedTestGroup(null);
      setHasTestSection(false);

      try {
        const course = selectedCourse.data ?? {};
        const courseGuid = getGuidCourseProgram(course) ?? course.guid;
        const referencedCourseGuid = course.type === COURSE_TYPES.REFERENCED ? course.guid : null;
        // Mismo motivo que en las unidades: sin `preview`, 403 y logout.
        const isDisregarded = selectedCourse.shouldUsePreview;
        const params = isDisregarded ? { preview: 1 } : undefined;

        const response = await getCourseResources({ courseGuid, referencedCourseGuid, params });
        if (isCancelled) return;

        const sections = response?.data?.data?.items ?? [];

        setHasTestSection(sections.some((section) => section.type === 'test'));

        const testGroups = parseTestGroups(sections);
        setTestSections(testGroups);

        if (testGroups.length === 1) {
          setSelectedTestGroup(testGroups[0]);
        }
      } catch (error) {
        if (isCancelled) return;
        console.error('[QuizLocationDialog] Error loading test sections:', error);
        setTestSections([]);
      } finally {
        setIsLoadingTestSections(false);
        if (!isCancelled) setHasLoadedTestSections(true);
      }
    }

    loadTestSections();

    return () => {
      isCancelled = true;
    };
  }, [isToolboxFlow, selectedCourse]);

  // Target `mint`: a unit is offered if it has a lesson for the page, or if it is itself one
  // (programs without lessons, where the unit is the lesson).
  const offeredUnits = useMemo(() => {
    if (!isMintTarget) return units;
    return units.filter((unit) => {
      const unitLessons = unit?.items ?? NO_ITEMS;
      if (unitLessons.length > 0) return filterLessons({ data: unit }).length > 0;
      return Boolean(unit?.lesson_guid) && isPowerLesson(unit);
    });
  }, [filterLessons, isMintTarget, isPowerLesson, units]);

  // Target `mint`: nothing is offered until the course detail says which lessons qualify, so the
  // teacher cannot pick one the detail then rules out.
  const isWaitingForCourseDetail = isMintTarget && isLoadingCourseDetail;

  const unitOptions = useMemo(() => {
    if (!selectedCourse || offeredUnits.length === 0 || isWaitingForCourseDetail) return NO_ITEMS;

    return offeredUnits.map((unit, index) => {
      const section = unit.section;
      const sectionGuid = (typeof section === 'object' ? section?.guid : unit.guid) || unit.guid;
      const sectionName = typeof section === 'object' ? section?.name : section;

      return {
        value: sectionGuid,
        label: decodeHtmlWithWhiteList(sectionName || `${t('chatbot:Unit')} ${index + 1}`),
        data: unit,
      };
    });
  }, [selectedCourse, offeredUnits, isWaitingForCourseDetail, t]);

  const lessonOptions = useMemo(() => {
    if (!selectedUnit || lessons.length === 0) return NO_ITEMS;

    return lessons.map((lesson, index) => ({
      value: lesson.guid,
      label: decodeHtmlWithWhiteList(lesson.lesson_name || `${t('chatbot:Lesson')} ${index + 1}`),
      data: lesson,
    }));
  }, [selectedUnit, lessons, t]);

  const resetSelections = useCallback(() => {
    setSelectedCourse(null);
    setSelectedUnit(null);
    setSelectedLesson(null);
    setUnits([]);
    setTestSections([]);
    setSelectedTestGroup(null);
    setHasTestSection(false);
    setHasLoadedUnits(false);
    setHasLoadedTestSections(false);
    setCourseDetail(null);
    setHasLoadedCourseDetail(false);
  }, []);

  // react-select dispara onChange también sobre la opción ya seleccionada y con la misma
  // referencia: sin el guard se limpiaría el estado sin relanzar el efecto de carga, dejando
  // el select de unidades vacío para siempre.
  // Target `mint`: a unit or lesson picked from the list data stops being valid if the course
  // detail rules it out; drop it instead of navigating to a lesson that cannot take the page.
  useEffect(() => {
    if (!isMintTarget) return;
    if (selectedUnit && !offeredUnits.includes(selectedUnit.data)) {
      setSelectedUnit(null);
      setSelectedLesson(null);
    } else if (selectedLesson && !lessons.includes(selectedLesson.data)) {
      setSelectedLesson(null);
    }
  }, [isMintTarget, lessons, offeredUnits, selectedLesson, selectedUnit]);

  const handleCourseChange = useCallback(
    (option) => {
      if (!option || option.value === selectedCourse?.value) return;
      resetSelections();
      setSelectedCourse(option);
    },
    [resetSelections, selectedCourse]
  );

  const handleTestGroupChange = useCallback((option) => {
    setSelectedTestGroup(option);
  }, []);

  const handleUnitChange = useCallback((option) => {
    setSelectedUnit(option);
    setSelectedLesson(null);
  }, []);

  const handleLessonChange = useCallback((option) => {
    setSelectedLesson(option);
  }, []);

  useEffect(() => {
    if (!isOpen) resetSelections();
  }, [isOpen, resetSelections]);

  const navigationUrl = useMemo(() => {
    if (!selectedCourse) return null;

    const courseGuid = selectedCourse.value;
    // Sin `preview=1` las vistas piden el detalle como si hubiera grupo de clase → 403.
    const shouldUsePreview = Boolean(selectedCourse.shouldUsePreview);

    function buildUrl(path, queryParams = []) {
      return buildLocationUrl(path, queryParams, shouldUsePreview);
    }

    if (isToolboxFlow) {
      if (!selectedTestGroup) return null;
      return buildUrl(`/course/${courseGuid}/create-evaluations/ia/${selectedTestGroup.sectionGuid}/${selectedTestGroup.groupGuid}`);
    }

    if (!selectedUnit) return null;

    // Target `mint`: the power lesson view opens the HTML page prefilled with the conversation.
    if (isMintTarget) {
      return getMintNavigationUrl({ conversationId, courseGuid, lessons, offeredUnits, selectedLesson, selectedUnit, shouldUsePreview });
    }

    if (lessons.length === 0) {
      // El `value` es el guid del item de curso; para navegar hace falta el de la lección.
      const unitLessonGuid = selectedUnit.data?.lesson_guid || selectedUnit.value;
      return buildUrl(`/course/${courseGuid}/lesson/${unitLessonGuid}/add/${CONTENT_TYPES.TEST}`, ['enableAI=true']);
    }

    if (!selectedLesson) return null;

    const lessonGuid = selectedLesson.data?.lesson_guid || selectedLesson.value;
    const lessonType = selectedLesson.data?.lesson_type;

    // En Edelvives aquí solo llegan las plantillas fija e híbrida, que abren con LessonViewPower.
    if (lessonType === LESSON_TYPES_IDS.POWER || isPowerCourse || isEdelvivesQuiz) {
      return buildUrl(`/course/${courseGuid}/lesson/${lessonGuid}`, ['addQuiz=true', 'enableAI=true']);
    }

    return buildUrl(`/course/${courseGuid}/lesson/${lessonGuid}/add/${CONTENT_TYPES.TEST}`, ['enableAI=true']);
  }, [
    selectedCourse,
    selectedUnit,
    selectedLesson,
    selectedTestGroup,
    isToolboxFlow,
    isPowerCourse,
    isEdelvivesQuiz,
    lessons,
    isMintTarget,
    conversationId,
    offeredUnits,
  ]);

  const canNavigate = useMemo(() => {
    if (isToolboxFlow) {
      if (!selectedCourse) return false;
      if (isLoadingTestSections) return false;
      if (testSections.length === 0) return false;
      if (testSections.length > 1) return Boolean(selectedTestGroup);
      return true;
    }

    if (isMintTarget) {
      return !isLoadingCourseDetail && Boolean(navigationUrl);
    }

    if (lessons.length === 0) {
      return Boolean(selectedCourse && selectedUnit);
    }

    return Boolean(selectedCourse && selectedLesson);
  }, [
    isToolboxFlow,
    selectedCourse,
    selectedUnit,
    selectedLesson,
    lessons,
    testSections,
    selectedTestGroup,
    isLoadingTestSections,
    isMintTarget,
    isLoadingCourseDetail,
    navigationUrl,
  ]);

  const handleConfirm = useCallback(() => {
    if (navigationUrl) {
      history.push(navigationUrl);
    }
    if (onNavigate) onNavigate();
    onClose();
  }, [history, navigationUrl, onClose, onNavigate]);

  const hasLessons = lessons.length > 0;
  const isLoadingLocations = isLoadingUnits || isLoadingCourseDetail;
  // Target `mint`: the course has content, but no lesson where an HTML page can be added.
  const hasNoMintLessons = isMintTarget && hasLoadedUnits && !isLoadingLocations && units.length > 0 && unitOptions.length === 0;
  const hasUnitsError = hasLoadedUnits && !isLoadingLocations && unitOptions.length === 0 && !hasNoMintLessons;
  // Solo si la unidad tiene lecciones: los programas de solo módulos no son un error.
  const hasLessonsError = Boolean(selectedUnit) && !isLoadingUnits && lessonOptions.length === 0 && hasLessons;
  const hasContentError = !isToolboxFlow && (hasUnitsError || hasLessonsError);
  const isUnitSelectDisabled = unitOptions.length === 0 && !isLoadingLocations;
  const isLessonSelectDisabled = lessonOptions.length === 0;

  // Distingue "no hay sección de test" de "la hay, pero sin grupos".
  const hasTestSectionsLoaded = isToolboxFlow && hasLoadedTestSections && !isLoadingTestSections;
  const hasNoTestSection = hasTestSectionsLoaded && !hasTestSection;
  const hasNoGroups = hasTestSectionsLoaded && hasTestSection && testSections.length === 0;
  const hasMultipleTestSections = isToolboxFlow && testSections.length > 1;

  // SelectCustomizable pinta el contenedor accesible del error: solo espera el texto.
  const unitError = hasUnitsError ? t('chatbot:teacher_tools_error_content_unavailable_title') : undefined;
  const lessonError = hasLessonsError ? t('chatbot:teacher_tools_error_content_unavailable_title') : undefined;

  const dialogTitle = isMintTarget ? t('chatbot:mint_location_dialog_title') : t('chatbot:quiz_location_dialog_title');
  const confirmText = isMintTarget ? t('chatbot:mint_location_confirm') : t('chatbot:quiz_location_create_quiz');

  return {
    // Options
    courseOptions,
    unitOptions,
    lessonOptions,
    testSections,

    // Selected values
    selectedCourse,
    selectedUnit,
    selectedLesson,
    selectedTestGroup,

    // Loading states
    isLoadingCourses,
    isLoadingUnits: isLoadingLocations,
    isLoadingTestSections,

    // Handlers
    handleCourseChange,
    handleUnitChange,
    handleLessonChange,
    handleTestGroupChange,
    handleConfirm,

    // Computed
    canNavigate,
    confirmText,
    dialogTitle,
    hasContentError,
    hasLessons,
    hasNoMintLessons,
    hasLessonsError,
    hasMultipleTestSections,
    hasNoGroups,
    hasNoTestSection,
    hasUnitsError,
    isLessonSelectDisabled,
    isUnitSelectDisabled,
    lessonError,
    unitError,

    // Expuestos solo para los tests: el componente no los consume.
    navigationUrl,
    resetSelections,

    // Flow
    isToolboxFlow,

    t,
  };
}

export default useQuizLocationDialog;
